import type { UserRole } from "../../types/auth";
import { providerApi } from "@/services/api/providerApi";
import { useAuthStore } from "@/stores/authStore";
import { useI18nStore } from "@/stores/i18nStore";
import {
  TourOperatorType,
  type TourOperatorAdminRegistrationRequest,
} from "@/types/provider";
import {
  Building,
  Check,
  Eye,
  EyeOff,
  FileText,
  MapPin,
  Phone,
  Plane,
  Shield,
} from "lucide-react";
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export const TourOperatorAdminRegisterPage: React.FC = () => {
  const [formData, setFormData] =
    useState<TourOperatorAdminRegistrationRequest>({
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      companyName: "",
      operatorType: TourOperatorType.TOUR_OPERATOR,
      address: "",
      phone: "",
      businessLicense: "",
    });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const { updateUser } = useAuthStore();
  const { t } = useI18nStore();

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await providerApi.registerTourOperatorAdmin(formData);

      // Update auth store with new user data
      updateUser({
        id: response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        role: response.user.role as UserRole,
      });

      // Navigate to tour operator dashboard
      navigate("/tour-admin");
    } catch (err: unknown) {
      const message = (
        err as { response?: { data?: { error?: { message?: string } } } }
      ).response?.data?.error?.message;
      setError(message || t.registrationFailed);
    } finally {
      setIsLoading(false);
    }
  };

  // The value is what the backend stores; only the label is translated.
  const operatorTypes = [
    { value: "TOUR_OPERATOR", label: t.auth2TourOperator },
    { value: "TRAVEL_AGENCY", label: t.auth2OpTravelAgency },
    { value: "CRUISE_LINE", label: t.auth2OpCruiseLine },
    { value: "AIRLINE", label: t.auth2OpAirline },
    { value: "HOTEL_CHAIN", label: t.auth2OpHotelChain },
    { value: "CAR_RENTAL", label: t.auth2OpCarRental },
    { value: "ADVENTURE_TOURS", label: t.auth2OpAdventureTours },
    { value: "CULTURAL_TOURS", label: t.auth2OpCulturalTours },
    { value: "BUSINESS_TRAVEL", label: t.auth2OpBusinessTravel },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-orange-50 flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center">
          <div className="flex items-center">
            <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-orange-600 rounded-lg flex items-center justify-center mr-3">
              <Plane className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold text-gray-900">HealthPass</span>
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t.auth2TourOperatorAdminTitle}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t.auth2TourOperatorAdminSubtitle}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-6 shadow-xl rounded-xl border border-gray-100">
          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Personal Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Shield className="w-5 h-5 mr-2 text-primary-600" />
                {t.personalInformation}
              </h3>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="firstName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.firstName}
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder={t.auth2FirstNamePlaceholder}
                    value={formData.firstName}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label
                    htmlFor="lastName"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    {t.lastName}
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder={t.auth2LastNamePlaceholder}
                    value={formData.lastName}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.email}
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder={t.auth2TravelEmailPlaceholder}
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.password}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    required
                    className="block w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder={t.auth2PasswordMinPlaceholder}
                    value={formData.password}
                    onChange={handleInputChange}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword ? t.auth2HidePassword : t.auth2ShowPassword
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <Eye className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Company Information */}
            <div className="space-y-4 border-t border-gray-200 pt-4">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <Building className="w-5 h-5 mr-2 text-orange-600" />
                {t.auth2CompanyInformation}
              </h3>

              <div>
                <label
                  htmlFor="companyName"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.auth2CompanyNameRequired}
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder={t.auth2TravelCompanyPlaceholder}
                  value={formData.companyName}
                  onChange={handleInputChange}
                />
              </div>

              <div>
                <label
                  htmlFor="operatorType"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  {t.auth2OperatorType}
                </label>
                <select
                  id="operatorType"
                  name="operatorType"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  value={formData.operatorType}
                  onChange={handleInputChange}
                >
                  {operatorTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  <MapPin className="w-4 h-4 inline mr-1" />
                  {t.address}
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                  placeholder={t.auth2AddressPlaceholder}
                  value={formData.address}
                  onChange={handleInputChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <Phone className="w-4 h-4 inline mr-1" />
                    {t.phone}
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder={t.auth2PhonePlaceholder}
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </div>
                <div>
                  <label
                    htmlFor="businessLicense"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    <FileText className="w-4 h-4 inline mr-1" />
                    {t.auth2BusinessLicense}
                  </label>
                  <input
                    id="businessLicense"
                    name="businessLicense"
                    type="text"
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
                    placeholder="LIC-12345"
                    value={formData.businessLicense}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Permissions Info */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-orange-800 mb-2">
                {t.auth2TourOperatorPermissions}
              </h4>
              <ul className="text-xs text-orange-700 space-y-1">
                <li>• {t.auth2TourPermTrips}</li>
                <li>• {t.auth2TourPermHealthRequirements}</li>
                <li>• {t.auth2TourPermStaff}</li>
                <li>• {t.auth2TourPermSupport}</li>
                <li>• {t.auth2TourPermReports}</li>
              </ul>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="text-red-800 text-sm text-center">{error}</div>
              </div>
            )}

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-primary-600 to-orange-600 hover:from-primary-700 hover:to-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    <span>{t.auth2Registering}</span>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <Check className="h-4 w-4 mr-2" />
                    <span>{t.auth2RegisterTourOperatorAdmin}</span>
                  </div>
                )}
              </button>
            </div>
          </form>

          {/* Navigation Links */}
          <div className="mt-6 space-y-3">
            <div className="text-center">
              <Link
                to="/register/provider"
                className="text-sm text-gray-600 hover:text-gray-500"
              >
                {t.auth2BackToProviderSelection}
              </Link>
            </div>
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                {t.auth2AlreadyHaveAccountSignIn}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
